import { useEffect, useMemo, useRef } from "react";
import { GeoJSONSource, Marker } from "mapbox-gl";
import iconAnchorPin from "../../img/map-anchor-pin.svg";
import {
	MapHelperInstance,
	useListenerSetupMapEffect,
	useStyleDependantMapEffect,
	useStyleSetupMapEffect,
} from "../../helper/map-helper";
import { LocatedVelavuDevice, LocationType } from "velavu-js-api";
import { geoJSONBlankCollection } from "./mapbox-injectable";
import { buildDeviceSymbolExpression } from "../../helper/map-device-symbols";
import { generateExpressionMetersToPixels } from "../../helper/mapbox-helper";
import { AnchorPositioningFeedback } from "../../state/pin-anchor-state";
import whitelabel from "../../whitelabel";

export interface MapboxInjectablePositionAnchorParams {
	mapInstance: MapHelperInstance;
	disabled?: boolean;
	otherAnchors: LocatedVelavuDevice[];
	position: [number, number] | null;
	setPosition: (position: [number, number] | null) => void;
	positioningFeedback: AnchorPositioningFeedback | undefined;
}

const mapboxPrefix = "mapbox-injectable-position-anchor-";
const mapboxIDMarkerRange = mapboxPrefix + "marker-range";
const mapboxIDMarkerRangeGlow = mapboxPrefix + "marker-range-glow";
const mapboxIDOtherAnchor = mapboxPrefix + "other-anchor";
const mapboxIDOtherAnchorRange = mapboxPrefix + "other-anchor-range";
const mapboxIDOtherAnchorRangeGlow = mapboxPrefix + "other-anchor-range-glow";

// The radius of the visualization circle around markers
// in meters
const VISUALIZATION_RADIUS = 6;

export default function useMapboxInjectablePositionAnchor(
	params: MapboxInjectablePositionAnchorParams,
) {
	const markerPosition = useMemo(() => {
		const markerElement = document.createElement("img");
		markerElement.style.pointerEvents = "none";
		markerElement.src = iconAnchorPin;

		return new Marker({
			element: markerElement,
			anchor: "bottom",
		});
	}, []);

	//Add the marker to the map while applicable
	const markerPositionIsOnMap = useRef(false);
	useEffect(() => {
		if (!params.mapInstance.map) {
			return;
		}

		if (params.position && !params.disabled) {
			markerPosition.setLngLat(params.position);

			if (!markerPositionIsOnMap.current) {
				markerPosition.addTo(params.mapInstance.map);
				markerPositionIsOnMap.current = true;
			}
		} else {
			if (markerPositionIsOnMap.current) {
				markerPosition.remove();
				markerPositionIsOnMap.current = false;
			}
		}
	}, [
		params.mapInstance.map,
		params.disabled,
		params.position,
		markerPosition,
	]);

	//Set the cursor to a crosshair while we are enabled
	useStyleDependantMapEffect(
		params.mapInstance,
		(map, box) => {
			if (!params.disabled) {
				map.getCanvas().style.cursor = "crosshair";
			} else {
				map.getCanvas().style.cursor = "";
			}
		},
		[params.disabled],
	);

	useListenerSetupMapEffect(
		params.mapInstance.map,
		(_, box) => {
			if (params.disabled) {
				return;
			}

			box.on("mousedown", (event) => {
				params.setPosition([event.lngLat.lng, event.lngLat.lat]);
			});
		},
		[params.disabled, params.setPosition],
	);

	useStyleSetupMapEffect(params.mapInstance, (_, box) => {
		box.addSource(mapboxIDMarkerRange, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});

		box.addSource(mapboxIDOtherAnchor, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});

		box.addLayer({
			id: mapboxIDOtherAnchor,
			type: "symbol",
			source: mapboxIDOtherAnchor,
			layout: {
				"icon-image": buildDeviceSymbolExpression(
					["get", "hardware"],
					["get", "online"],
					["==", ["get", "locationType"], LocationType.Fixed],
				),
				"icon-allow-overlap": true,
				"icon-size": 0.7,
			},
		});

		box.addLayer(
			{
				id: mapboxIDMarkerRange,
				type: "circle",
				source: mapboxIDMarkerRange,
				paint: {
					"circle-radius": generateExpressionMetersToPixels(
						VISUALIZATION_RADIUS,
						["get", "latitude"],
					),
					"circle-opacity": 0,
					"circle-stroke-color": ["get", "color"],
					"circle-stroke-width": 2,
					"circle-pitch-alignment": "map",
				},
			},
			mapboxIDOtherAnchor,
		);

		box.addLayer(
			{
				id: mapboxIDMarkerRangeGlow,
				type: "circle",
				source: mapboxIDMarkerRange,
				paint: {
					"circle-radius": generateExpressionMetersToPixels(
						VISUALIZATION_RADIUS,
						["get", "latitude"],
					),
					"circle-opacity": 0,
					"circle-stroke-color": ["get", "color"],
					"circle-stroke-width": 2,
					"circle-stroke-opacity": 0.3,
					"circle-pitch-alignment": "map",
					"circle-blur": -0.5,
				},
			},
			mapboxIDMarkerRange,
		);

		box.addLayer(
			{
				id: mapboxIDOtherAnchorRange,
				type: "circle",
				source: mapboxIDOtherAnchor,
				paint: {
					"circle-radius": generateExpressionMetersToPixels(
						VISUALIZATION_RADIUS,
						["get", "latitude"],
					),
					"circle-opacity": 0,
					"circle-stroke-color": whitelabel.palette.secondary500,
					"circle-stroke-width": 2,
					"circle-pitch-alignment": "map",
				},
			},
			mapboxIDMarkerRangeGlow,
		);

		box.addLayer(
			{
				id: mapboxIDOtherAnchorRangeGlow,
				type: "circle",
				source: mapboxIDOtherAnchor,
				paint: {
					"circle-radius": generateExpressionMetersToPixels(
						VISUALIZATION_RADIUS,
						["get", "latitude"],
					),
					"circle-opacity": 0,
					"circle-stroke-color": whitelabel.palette.secondary500,
					"circle-stroke-width": 2,
					"circle-stroke-opacity": 0.3,
					"circle-pitch-alignment": "map",
					"circle-blur": -0.5,
				},
			},
			mapboxIDOtherAnchorRange,
		);
	});

	useStyleDependantMapEffect(
		params.mapInstance,
		(map) => {
			const anchorsSource = map.getSource(
				mapboxIDOtherAnchor,
			) as GeoJSONSource;
			anchorsSource.setData({
				type: "FeatureCollection",
				features: params.otherAnchors.map((device) => ({
					type: "Feature",
					id: device.id,
					geometry: {
						type: "Point",
						coordinates: device.location.coordinates,
					},
					properties: {
						latitude: device.location.coordinates[1],
						online: device.online,
						hardware: device.hardware,
						locationType: device.location.location_type,
					},
				})),
			});
		},
		[params.otherAnchors],
	);

	//Update the marker range source when applicable
	useStyleDependantMapEffect(
		params.mapInstance,
		(map) => {
			const markerRangeSource = map.getSource(
				mapboxIDMarkerRange,
			) as GeoJSONSource;

			if (params.position && !params.disabled) {
				let color: string;
				switch (params.positioningFeedback) {
					case AnchorPositioningFeedback.Close:
						color = whitelabel.palette.warning900;
						break;
					case AnchorPositioningFeedback.OK:
						color = whitelabel.palette.active900;
						break;
					case AnchorPositioningFeedback.Far:
						color = whitelabel.palette.error900;
						break;
					default:
						color = whitelabel.palette.secondary500;
						break;
				}

				markerRangeSource.setData({
					type: "Feature",
					geometry: {
						type: "Point",
						coordinates: params.position,
					},
					properties: {
						latitude: params.position[1],
						color: color,
					},
				});
			} else {
				markerRangeSource.setData(geoJSONBlankCollection);
			}
		},
		[
			params.mapInstance.map,
			params.disabled,
			params.position,
			params.positioningFeedback,
		],
	);
}
